import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import * as A from './mediaLibraryActions';
import type { MediaLibraryUploadPayload, Deferred } from './mediaLibraryTypes';
import type { DisplayOrientation } from '../../../components/display/data/types';

export function useMediaLibraryStore() {
  const dispatch = useAppDispatch();
  const { items, loading, error } = useAppSelector((s) => s.mediaLibrary);

  const fetchMedia = useCallback((locationId: string) => dispatch(A.mediaLibraryFetchRequest({ locationId })), [dispatch]);
  const addMedia = useCallback((payload: MediaLibraryUploadPayload) => dispatch(A.mediaLibraryAdd(payload)), [dispatch]);

  const uploadMedia = useCallback(
    (locationId: string, file: File, orientation: DisplayOrientation, onProgress?: (percent: number) => void): Promise<MediaLibraryUploadPayload> => {
      return new Promise<MediaLibraryUploadPayload>((resolve, reject) => {
        const deferred: Deferred<MediaLibraryUploadPayload> = { resolve, reject };
        dispatch(A.mediaLibraryUploadRequest({ locationId, file, orientation, deferred, onProgress }));
      });
    },
    [dispatch],
  );

  const deleteMedia = useCallback(
    (id: string, locationId: string): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        const deferred: Deferred = { resolve, reject };
        dispatch(A.mediaLibraryDeleteRequest({ id, locationId, deferred }));
      });
    },
    [dispatch],
  );

  return useMemo(
    () => ({ items, loading, error, fetchMedia, addMedia, uploadMedia, deleteMedia }),
    [items, loading, error, fetchMedia, addMedia, uploadMedia, deleteMedia],
  );
}
